import { MONTHS } from '@constants/calendar';
import { CtaButton } from '@tkww/orion-web-ds-action';
import {
	DatePicker,
	Radio,
	Select,
	SingleSelectionGroup,
} from '@tkww/orion-web-ds-input';
import { TextBody } from '@tkww/orion-web-ds-text';
import { compose } from '@tkww/orion-web-lib-css-modules';
import type { ListboxController } from '@tkww/orion-web-lib-listbox';
import React, { useState } from 'react';
import { decisionArea } from '../../../hooks/trackingConstants';
import { useTrackEstimateStepNext } from '../../../hooks/useTrackEstimateStepNext';
import {
	type WeddingDateData,
	YEARS,
	getWeddingDateError,
	isDateOption,
} from '../../../utils/weddingDate';
import Styles from './styles.scss';

export interface WeddingDateStepProps {
	weddingDate: WeddingDateData;
	onChange: (data: WeddingDateData) => void;
	onNext: () => void;
	buttonLabel?: string;
}

export const WeddingDateStep = ({
	weddingDate,
	onChange,
	onNext,
	buttonLabel = 'Next',
}: WeddingDateStepProps) => {
	const { dateOption, specificDate, month, year } = weddingDate;

	const [error, setError] = useState<string | undefined>(undefined);
	const [dateTouched, setDateTouched] = useState(false);

	const validate = (data: WeddingDateData = weddingDate) => {
		const err = getWeddingDateError(data);
		if (err) {
			setError(err);
			return false;
		}
		setError(undefined);
		return true;
	};

	const handleOptionChange = (e: SingleSelectionGroup.SelectedChangeEvent) => {
		const value = e.detail.value;
		if (!isDateOption(value)) return;
		setError(undefined);
		setDateTouched(false);
		onChange({ ...weddingDate, dateOption: value });
	};

	const handleDateChange: NonNullable<DatePicker.OwnProps['onChange']> = (
		e,
	) => {
		const { month: m, day, year: y } = e.detail.value;
		const monthNum = Number(m);
		const monthIndex = m && monthNum >= 1 && monthNum <= 12 ? monthNum - 1 : -1;
		const isComplete = m && day && y && y.length === 4;
		const newData: WeddingDateData = {
			...weddingDate,
			specificDate: isComplete
				? `${y}-${m.padStart(2, '0')}-${day.padStart(2, '0')}`
				: '',
			month: monthIndex >= 0 ? MONTHS[monthIndex] : '',
			year: y || '',
		};
		if (isComplete) {
			setDateTouched(true);
			validate(newData);
		} else if (dateTouched) {
			validate(newData);
		} else {
			setError(undefined);
		}
		onChange(newData);
	};

	const handleMonthChange = (e: ListboxController.SelectedChangeEvent) => {
		const newData = {
			...weddingDate,
			month: e.detail.value?.toString() || '',
		};
		newData.month && newData.year ? validate(newData) : setError(undefined);
		onChange(newData);
	};

	const handleYearChange = (e: ListboxController.SelectedChangeEvent) => {
		const newData = {
			...weddingDate,
			year: e.detail.value?.toString() || '',
		};
		newData.month && newData.year ? validate(newData) : setError(undefined);
		onChange(newData);
	};

	const trackWeddingDateNext = useTrackEstimateStepNext();

	const handleNext = () => {
		if (!validate() || !isDateOption(dateOption)) return;
		trackWeddingDateNext(decisionArea.WEDDING_DATE, dateOption);
		onNext();
	};

	return (
		<div className={Styles.weddingDateStep} data-testid="wedding-date-step">
			<div className={Styles.header}>
				<TextBody size="xl" bold>
					Have a wedding date in mind?
				</TextBody>
				<TextBody size="sm" color="subtle-dark">
					Rates often fluctuate by season or day of the week. This helps us
					calculate the most accurate price.
				</TextBody>
			</div>
			<SingleSelectionGroup
				selected={dateOption}
				onSelectedChange={handleOptionChange}
			>
				<div className={Styles.radioList}>
					<div className={Styles.radioOption}>
						<Radio value="specificDate" data-testid="radio-specific-date">
							We picked a date
						</Radio>
						<div
							className={
								dateOption !== 'specificDate' ? Styles.hidden : undefined
							}
						>
							<DatePicker
								label="Wedding date"
								name="weddingDate"
								dialogClasses={compose({
									container: Styles.datePickerDialog,
								})}
								value={
									specificDate
										? {
												month: specificDate.slice(5, 7),
												day: specificDate.slice(8, 10),
												year: specificDate.slice(0, 4),
											}
										: { month: '', day: '', year: '' }
								}
								onChange={handleDateChange}
								state={
									error && dateOption === 'specificDate' ? 'invalid' : 'neutral'
								}
								subText={dateOption === 'specificDate' ? error : undefined}
								data-testid="specific-date-input"
							/>
						</div>
					</div>
					<div className={Styles.radioOption}>
						<Radio value="monthYear" data-testid="radio-month-year">
							We have a month and year in mind
						</Radio>
						<div
							className={dateOption !== 'monthYear' ? Styles.hidden : undefined}
						>
							<div className={Styles.monthYearSelects}>
								<Select
									label="Month"
									name="month"
									options={MONTHS.map((m) => ({ value: m }))}
									selected={month}
									onSelectedChange={handleMonthChange}
									state={
										error && dateOption === 'monthYear' ? 'invalid' : 'neutral'
									}
									data-testid="month-select"
								/>
								<Select
									label="Year"
									name="year"
									options={YEARS.map((y) => ({ value: y }))}
									selected={year}
									onSelectedChange={handleYearChange}
									state={
										error && dateOption === 'monthYear' ? 'invalid' : 'neutral'
									}
									subText={dateOption === 'monthYear' ? error : undefined}
									data-testid="year-select"
								/>
							</div>
						</div>
					</div>
				</div>
			</SingleSelectionGroup>
			<CtaButton
				className={Styles.ctaButton}
				styleType="primary"
				size="lg"
				onClick={handleNext}
				disabled={!!getWeddingDateError(weddingDate)}
				data-testid="wedding-date-next-button"
			>
				{buttonLabel}
			</CtaButton>
		</div>
	);
};
